// Shared, tile-agnostic pixel-drawing primitives.
//
// These are lifted verbatim (minus the tile renderer's SHADOW_PASS bookkeeping)
// from js/modules/roomImageGen.js so that other pixel-art generators — the
// avatar portraits in avatarGen.js, for one — can reuse the same tiny rect/disc
// API without importing the whole map-tile renderer (region themes, recipes,
// animation loop). roomImageGen.js keeps its own copies for now; if these two
// ever diverge, reconcile here first. Deduping the tile renderer onto this
// module is a deliberate follow-up, kept out of scope to avoid touching the
// working map pipeline.

// makeG(ctx) -> a minimal drawing API over a 2D context.
//   g.r(x, y, w, h, color)  fill a rectangle (coords floored to whole pixels)
//   g.a(alpha, fn)          run fn with a temporary globalAlpha, then restore
// The context only needs fillStyle, fillRect and globalAlpha, so a lightweight
// recording stub works in tests where no real canvas exists.
export function makeG(ctx) {
  return {
    r(x, y, w, h, c) {
      ctx.fillStyle = c;
      ctx.fillRect(x | 0, y | 0, w | 0, h | 0);
    },
    a(alpha, fn) {
      ctx.globalAlpha = alpha;
      fn();
      ctx.globalAlpha = 1;
    },
  };
}

// Random integer in [min, max] from a 0..1 rng.
export const ri = (rng, min, max) => min + Math.floor(rng() * (max - min + 1));

// Filled disc, drawn scanline by scanline as stacked 1px rects.
export function disc(g, cx, cy, r, c) {
  for (let dy = -r; dy <= r; dy++) {
    const dx = Math.floor(Math.sqrt(r * r - dy * dy));
    g.r(cx - dx, cy + dy, 2 * dx + 1, 1, c);
  }
}

// Disc outline (one pixel on each side per row).
export function ringO(g, cx, cy, r, c) {
  for (let dy = -r; dy <= r; dy++) {
    const dx = Math.round(Math.sqrt(r * r - dy * dy));
    g.r(cx - dx, cy + dy, 1, 1, c);
    g.r(cx + dx, cy + dy, 1, 1, c);
  }
}

// Fill a rect with c1, then sprinkle c2 pixels over it for a stippled blend.
export function dither(g, rng, x, y, w, h, c1, c2, density = 0.2) {
  g.r(x, y, w, h, c1);
  const n = Math.floor(w * h * density);
  for (let i = 0; i < n; i++) g.r(x + rng() * w, y + rng() * h, 1, 1, c2);
}

// Axis-aligned 1px lines — thin convenience wrappers over g.r.
export function hLine(g, x, y, len, c) {
  g.r(x, y, len, 1, c);
}
export function vLine(g, x, y, len, c) {
  g.r(x, y, 1, len, c);
}

// Nearest-neighbour upscale: draw a small logical canvas onto a larger output
// canvas with smoothing off, for crisp pixel-art at display size. Returns the
// output canvas. Browser-only (needs a real 2D context / drawImage).
export function upscaleCanvas(srcCanvas, size, makeCanvas) {
  const out = makeCanvas(size, size);
  const octx = out.getContext("2d");
  octx.imageSmoothingEnabled = false;
  octx.drawImage(srcCanvas, 0, 0, size, size);
  return out;
}
